import { useMemo, useState } from 'react';
import {
  newId,
  nextCircuitCode,
  type Circuit,
  type Element,
  type Id,
  type Op,
  type Project,
  localeTag,
} from '@elec/domain';
import {
  analyse,
  effectiveRcd,
  isCountedPoint,
  RCD_DEVICES,
  type Analysis,
  type CircuitInfo,
} from '@elec/analysis';
import { appendCircuitOps, renumberOps, type CircuitSpec, type Editor } from '@elec/editor';
import { contentLocale, pick, type Locale as UiLocale, type MessageKey, type Translate } from '@elec/i18n';
import { ruleInfo, type Finding, type Severity } from '@elec/rules';
import { getSymbol } from '@elec/symbols';
import { INSTALL_METHODS, projectCalcs, type CircuitCalc } from '@elec/calc';
import { findingAction, type FindingButton, type FindingGoto } from './findingActions.ts';
import type { QuickFixKind } from './quickFixes.ts';

export type PanelTab = 'circuits' | 'checks';

const SECTIONS = [1.5, 2.5, 4, 6, 10, 16];

function CellInput(props: {
  value: string;
  onCommit(v: string): void;
  width?: number;
  placeholder?: string;
  type?: 'text' | 'number';
  testId?: string;
  /** Accessible name (the column and the row: "Calibre B"). */
  label?: string;
}) {
  const [v, setV] = useState(props.value);
  const [focused, setFocused] = useState(false);
  const shown = focused ? v : props.value;
  return (
    <input
      className="cell"
      style={props.width ? { width: props.width } : undefined}
      type={props.type ?? 'text'}
      value={shown}
      placeholder={props.placeholder}
      aria-label={props.label}
      data-testid={props.testId}
      onFocus={() => {
        setV(props.value);
        setFocused(true);
      }}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => {
        setFocused(false);
        if (v !== props.value) props.onCommit(v);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setV(props.value);
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
}

const n1 = (v: number, locale: 'fr' | 'en' | 'nl') =>
  (Math.round(v * 10) / 10).toLocaleString(localeTag(locale));

/** Ib, Iz, voltage drop and maximum length, with the failing values highlighted. */
function CalcCells({
  k,
  locale,
  t,
  code,
}: {
  k: CircuitCalc | undefined;
  locale: 'fr' | 'en' | 'nl';
  t: Translate;
  code: string;
}) {
  if (!k) return <td colSpan={4} />;
  const dash = <span className="muted">—</span>;
  const ibBad = k.ib !== undefined && k.inA !== undefined && k.ib > k.inA;
  const izBad = k.iz !== undefined && k.inA !== undefined && k.inA > k.iz;
  const dropBad = k.dropPct !== undefined && k.dropPct > k.dropLimitPct;
  const lBad = k.lengthM !== undefined && k.lMaxM !== undefined && k.lengthM > k.lMaxM;
  return (
    <>
      <td
        className={`num${ibBad ? ' warn' : ''}`}
        title={t(k.ibSource === 'loads' ? 'circ.ibLoads' : 'circ.ibRating')}
        data-testid={`ib-${code}`}
      >
        {k.ib !== undefined ? n1(k.ib, locale) : dash}
      </td>
      <td className={`num${izBad ? ' bad' : ''}`}>{k.iz !== undefined ? n1(k.iz, locale) : dash}</td>
      <td
        className={`num${dropBad ? ' warn' : ''}`}
        title={t('circ.dropLimit', { pct: k.dropLimitPct })}
        data-testid={`drop-${code}`}
      >
        {k.dropPct !== undefined ? `${n1(k.dropPct, locale)} %` : dash}
      </td>
      <td className={`num${lBad ? ' bad' : ''}`} title={t('circ.lmaxHint')}>
        {k.lMaxM !== undefined ? `${Math.floor(k.lMaxM)} m` : dash}
      </td>
    </>
  );
}

/** Ops to update a circuit's stored data, creating and linking the circuit record if needed. */
function circuitPatch(project: Project, info: CircuitInfo, patch: Partial<Omit<Circuit, 'id'>>): Op[] {
  if (info.circuit) return [{ op: 'update', coll: 'circuits', id: info.circuit.id, patch }];
  const id = newId();
  const c: Circuit = {
    id,
    code: nextCircuitCode(project),
    label: '',
    cable: { type: info.cable.type, sectionMm2: info.cable.sectionMm2, cores: info.cable.cores || 3 },
    legacy: false,
    ...patch,
  };
  return [
    { op: 'add', coll: 'circuits', value: c },
    { op: 'update', coll: 'elements', id: info.device.id, patch: { circuitId: id } },
  ];
}

function CircuitTable({
  editor,
  project,
  analysis,
  t,
  locale,
  onShow,
}: {
  editor: Editor;
  project: Project;
  analysis: Analysis;
  t: Translate;
  /** The interface language (the project's own language is for its documents). */
  locale: 'fr' | 'en' | 'nl';
  onShow(ids: Id[], sheetId: Id): void;
}) {
  const singleLine = Object.values(project.sheets).filter((s) => s.kind === 'single-line');
  const targetSheet =
    project.sheets[editor.sheetId]?.kind === 'single-line' ? editor.sheetId : singleLine[0]?.id;
  const rcds = useMemo(
    () =>
      Object.values(project.elements).filter(
        (e) => e.sheetId === targetSheet && RCD_DEVICES.has(e.symbolId) && e.symbolId !== 'iec.rcbo',
      ),
    [project, targetSheet],
  );
  const [under, setUnder] = useState<string>('');
  const [kind, setKind] = useState<'sockets' | 'lights' | 'appliance'>('sockets');
  const edit = (ops: Op[]) => ops.length && editor.history.dispatch({ label: t('tx.circuit'), ops });
  const setDevice = (el: Element, key: string, value: string | number) =>
    edit([{ op: 'update', coll: 'elements', id: el.id, patch: { props: { ...el.props, [key]: value } } }]);

  const addCircuit = () => {
    if (!targetSheet) return;
    const spec: CircuitSpec =
      kind === 'sockets'
        ? {
            label: t('circ.kindSockets'),
            device: { symbolId: 'iec.breaker', props: { ratingA: 20, curve: 'C' } },
            points: [{ symbolId: 'iec.socket' }, { symbolId: 'iec.socket' }],
            cable: { type: 'XVB', cores: 3, sectionMm2: 2.5 },
          }
        : kind === 'lights'
          ? {
              label: t('circ.kindLights'),
              device: { symbolId: 'iec.breaker', props: { ratingA: 16, curve: 'B' } },
              points: [{ symbolId: 'iec.light' }, { symbolId: 'iec.light' }],
              cable: { type: 'XVB', cores: 3, sectionMm2: 1.5 },
            }
          : {
              label: t('circ.kindAppliance'),
              device: { symbolId: 'iec.breaker', props: { ratingA: 20, curve: 'C' } },
              points: [{ symbolId: 'iec.appliance' }],
              cable: { type: 'XVB', cores: 3, sectionMm2: 2.5 },
            };
    const rcdId = (under && rcds.some((r) => r.id === under) ? under : undefined) ?? rcds.at(-1)?.id;
    editor.history.dispatch({
      label: t('tx.addCircuit'),
      ops: appendCircuitOps(project, targetSheet, spec, rcdId, analysis),
    });
    editor.setSheet(targetSheet);
  };

  return (
    <div className="circuit-table">
      <div className="panel-actions">
        <label>
          {t('ai.target')}{' '}
          <select
            value={under || rcds.at(-1)?.id || ''}
            onChange={(e) => setUnder(e.target.value)}
            disabled={!targetSheet}
            data-testid="add-under"
          >
            {rcds.length === 0 && <option value="">{t('circ.mainBus')}</option>}
            {rcds.map((r) => (
              <option key={r.id} value={r.id}>
                {r.tag} · {String(r.props.sensitivityMa ?? '')} mA
              </option>
            ))}
          </select>
        </label>
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as typeof kind)}
          disabled={!targetSheet}
          aria-label={t('circ.add')}
        >
          <option value="sockets">{t('circ.kindSockets')}</option>
          <option value="lights">{t('circ.kindLights')}</option>
          <option value="appliance">{t('circ.kindAppliance')}</option>
        </select>
        <button className="primary" onClick={addCircuit} disabled={!targetSheet} data-testid="add-circuit">
          {t('circ.add')}
        </button>
        <button onClick={() => edit(renumberOps(project, analysis))} data-testid="renumber">
          {t('circ.renumber')}
        </button>
        {!targetSheet && <span className="muted">{t('circ.noSingleLine')}</span>}
      </div>
      {analysis.circuits.length === 0 ? (
        <p className="muted pad">{t('circ.empty')}</p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                {(
                  [
                    'circ.code',
                    'circ.label',
                    'circ.protection',
                    'circ.rcd',
                    'circ.cableType',
                    'circ.cores',
                    'circ.section',
                    'circ.length',
                    'circ.method',
                    'circ.ib',
                    'circ.iz',
                    'circ.drop',
                    'circ.lmax',
                    'circ.points',
                    'circ.legacy',
                  ] as MessageKey[]
                ).map((k) => (
                  <th key={k}>{t(k)}</th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {analysis.circuits.map((c) => {
                const def = getSymbol(c.device.symbolId)!;
                const k = projectCalcs(project, analysis).get(c.device.id);
                const curveField = def.fields?.find((f) => f.key === 'curve');
                const rcd = effectiveRcd(c);
                const counted = c.points.filter((p) => isCountedPoint(p.kind)).length;
                const cable = c.circuit?.cable ?? {
                  type: c.cable.type,
                  cores: c.cable.cores,
                  sectionMm2: c.cable.sectionMm2,
                };
                const name = c.code || c.device.tag;
                const al = (k: MessageKey) => `${t(k)} ${name}`;
                return (
                  <tr key={c.device.id} data-testid={`circuit-row-${c.code || c.device.tag}`}>
                    <td className="code">{c.code || <span className="muted">{c.device.tag}</span>}</td>
                    <td>
                      <CellInput
                        value={c.circuit?.label ?? ''}
                        width={170}
                        onCommit={(v) => edit(circuitPatch(project, c, { label: v.trim() }))}
                        testId={`label-${c.code}`}
                        label={al('circ.label')}
                      />
                      {c.circuit?.spare && (
                        <button
                          className="badge"
                          title={t('circ.spare')}
                          onClick={() => edit(circuitPatch(project, c, { spare: false }))}
                          data-testid={`spare-${c.code}`}
                        >
                          {t('circ.spare')} ×
                        </button>
                      )}
                    </td>
                    <td className="nowrap">
                      {curveField && (
                        <select
                          value={String(c.device.props.curve ?? 'C')}
                          aria-label={`${t('circ.protection')} ${name}`}
                          onChange={(e) => setDevice(c.device, 'curve', e.target.value)}
                        >
                          {['B', 'C', 'D'].map((v) => (
                            <option key={v}>{v}</option>
                          ))}
                        </select>
                      )}
                      <CellInput
                        type="number"
                        width={52}
                        value={String(c.ratingA ?? '')}
                        onCommit={(v) => setDevice(c.device, 'ratingA', Number(v))}
                        testId={`rating-${c.code}`}
                        label={`${t('circ.protection')} ${name} (A)`}
                      />{' '}
                      A
                    </td>
                    <td className="nowrap">
                      {rcd ? `${rcd.sensitivityMa} mA ${rcd.type}` : <span className="warn">—</span>}
                    </td>
                    <td>
                      <CellInput
                        value={cable.type}
                        width={64}
                        placeholder="XVB"
                        label={al('circ.cableType')}
                        onCommit={(v) =>
                          edit(circuitPatch(project, c, { cable: { ...cable, type: v.trim() } }))
                        }
                      />
                    </td>
                    <td>
                      <CellInput
                        type="number"
                        width={44}
                        value={String(cable.cores || 3)}
                        label={al('circ.cores')}
                        onCommit={(v) =>
                          edit(circuitPatch(project, c, { cable: { ...cable, cores: Number(v) } }))
                        }
                      />
                    </td>
                    <td>
                      <select
                        value={String(cable.sectionMm2 || '')}
                        onChange={(e) =>
                          edit(
                            circuitPatch(project, c, {
                              cable: { ...cable, sectionMm2: Number(e.target.value) },
                            }),
                          )
                        }
                        data-testid={`section-${c.code}`}
                        aria-label={al('circ.section')}
                        aria-invalid={!(cable.sectionMm2 > 0) || undefined}
                      >
                        <option value="">—</option>
                        {SECTIONS.map((s) => (
                          <option key={s} value={String(s)}>
                            {String(s).replace('.', locale === 'en' ? '.' : ',')}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className={c.circuit?.cable.lengthEstimated ? 'estimated' : undefined}>
                      <CellInput
                        type="number"
                        width={56}
                        value={c.circuit?.cable.lengthM ? String(c.circuit.cable.lengthM) : ''}
                        onCommit={(v) =>
                          // A length typed in is measured: no longer an estimate.
                          edit(
                            circuitPatch(project, c, {
                              cable: {
                                ...cable,
                                lengthM: v ? Number(v) : undefined,
                                lengthEstimated: undefined,
                              },
                            }),
                          )
                        }
                        testId={`length-${c.code}`}
                        label={al('circ.length')}
                      />
                      {c.circuit?.cable.lengthEstimated && (
                        <span
                          className="estimate"
                          title={t('circ.estimated')}
                          data-testid={`estimated-${c.code}`}
                        >
                          ≈
                        </span>
                      )}
                    </td>
                    <td>
                      <select
                        value={k?.method ?? 'B1'}
                        aria-label={al('circ.method')}
                        title={t(`circ.method.${k?.method ?? 'B1'}` as MessageKey)}
                        onChange={(e) =>
                          edit(
                            circuitPatch(project, c, { cable: { ...cable, installMethod: e.target.value } }),
                          )
                        }
                      >
                        {INSTALL_METHODS.map((m) => (
                          <option key={m} value={m} title={t(`circ.method.${m}` as MessageKey)}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </td>
                    <CalcCells k={k} locale={locale} t={t} code={c.code} />
                    <td className={counted > 8 ? 'warn' : ''}>
                      {c.points.length}
                      {c.points.length
                        ? ` (${[...new Set(c.points.map((p) => p.element.tag))].slice(0, 4).join(', ')}${c.points.length > 4 ? '…' : ''})`
                        : ''}
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!c.circuit?.legacy}
                        aria-label={al('circ.legacy')}
                        onChange={(e) => edit(circuitPatch(project, c, { legacy: e.target.checked }))}
                      />
                    </td>
                    <td>
                      <button
                        className="link"
                        onClick={() =>
                          onShow([c.device.id, ...c.points.map((p) => p.element.id)], c.sheet.id)
                        }
                      >
                        {t('circ.show')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const GOTO_LABEL: Record<FindingGoto, MessageKey> = {
  projectInfo: 'checks.goProjectInfo',
  measurements: 'checks.goMeasurements',
  circuits: 'checks.goCircuits',
  properties: 'checks.goProperties',
  update: 'ml.update',
  survey: 'survey.generate',
  sheet: 'checks.goSheet',
  show: 'checks.goShow',
  newPlan: 'checks.goNewPlan',
  addEarth: 'checks.goAddEarth',
  renumber: 'checks.goRenumber',
  numberTerminals: 'checks.goNumberTerminals',
  toA3: 'checks.goA3',
  earthSockets: 'checks.goEarthSockets',
  rename: 'checks.goRename',
  attach: 'checks.goAttach',
  drawRoom: 'checks.goDrawRoom',
  delete: 'checks.goDelete',
  spare: 'checks.goSpare',
  estimateLengths: 'checks.goEstimate',
  photo: 'checks.goPhoto',
  blankingOk: 'checks.goBlankingOk',
  balance: 'checks.goBalance',
  addNorth: 'checks.goAddNorth',
  drawRoutes: 'checks.goDrawRoutes',
  quick: 'checks.goQuick',
};

const QUICK_LABEL: Record<QuickFixKind, MessageKey> = {
  move30: 'checks.quickMove30',
  typeA: 'checks.quickTypeA',
  main300: 'checks.quickMain300',
  rating: 'checks.quickRating',
  section: 'checks.quickSection',
};

const SEV_ICON: Record<Severity, string> = { error: '●', warning: '▲', info: 'ℹ' };

export type RuleReportReason = 'wrong' | 'not-applicable' | 'wording' | 'other';
/** How the check panel sends a rule report: `signin` when an account is needed first. */
export type RuleReporter =
  | { kind: 'send'; send(f: Finding, reason: RuleReportReason, comment: string): Promise<void> }
  | { kind: 'signin'; signIn(): void };

const REASONS: Array<{ value: RuleReportReason; key: MessageKey }> = [
  { value: 'wrong', key: 'checks.reportWrong' },
  { value: 'not-applicable', key: 'checks.reportNotApplicable' },
  { value: 'wording', key: 'checks.reportWording' },
  { value: 'other', key: 'checks.reportOther' },
];

/** "Report this rule": why it is wrong and a comment, sent to Tablo with the rule's id. */
function ReportRule({ f, t, reporter }: { f: Finding; t: Translate; reporter: RuleReporter }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<RuleReportReason>('wrong');
  const [comment, setComment] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  if (state === 'sent')
    return (
      <p className="small ok" data-testid={`report-sent-${f.ruleId}`}>
        {t('checks.reportThanks')}
      </p>
    );
  if (!open)
    return (
      <button className="link small" onClick={() => setOpen(true)} data-testid={`report-${f.ruleId}`}>
        {t('checks.report')}
      </button>
    );
  if (reporter.kind === 'signin')
    return (
      <p className="small">
        {t('checks.reportSignIn')}{' '}
        <button className="link" onClick={reporter.signIn}>
          {t('checks.reportSignInButton')}
        </button>
      </p>
    );
  const send = async () => {
    setState('sending');
    try {
      await reporter.send(f, reason, comment.trim());
      setState('sent');
    } catch {
      setState('error');
    }
  };
  return (
    <div className="report-rule" data-testid={`report-form-${f.ruleId}`}>
      <p className="small">
        <strong>{t('checks.reportTitle', { rule: f.ruleId })}</strong>
      </p>
      <div
        className="report-reasons"
        role="radiogroup"
        aria-label={t('checks.reportTitle', { rule: f.ruleId })}
      >
        {REASONS.map((r) => (
          <label key={r.value} className="small">
            <input
              type="radio"
              name={`reason-${f.ruleId}`}
              checked={reason === r.value}
              onChange={() => setReason(r.value)}
            />{' '}
            {t(r.key)}
          </label>
        ))}
      </div>
      <textarea
        rows={2}
        maxLength={2000}
        value={comment}
        placeholder={t('checks.reportComment')}
        onChange={(e) => setComment(e.target.value)}
        aria-label={t('checks.reportComment')}
      />
      <p className="muted small">{t('checks.reportPrivacy')}</p>
      {state === 'error' && <p className="small bad">{t('checks.reportError')}</p>}
      <div className="row">
        <button className="fix-btn" disabled={state === 'sending'} onClick={() => void send()}>
          {t('checks.reportSend')}
        </button>
        <button className="link" onClick={() => setOpen(false)}>
          {t('dialog.cancel')}
        </button>
      </div>
    </div>
  );
}

function Checks({
  findings,
  project,
  t,
  locale,
  onShow,
  onFix,
  onGoto,
  reporter,
}: {
  findings: Finding[];
  project: Project;
  t: Translate;
  /** The interface language: findings are read by the user, not printed in the project. */
  locale: UiLocale;
  onShow(ids: Id[], sheetId: Id | undefined): void;
  onFix?(f: Finding): void;
  onGoto?(f: Finding, b: FindingButton, pick?: string): void;
  reporter?: RuleReporter;
}) {
  const colon = locale === 'fr' ? ' : ' : ': ';
  const [open, setOpen] = useState<number | null>(null);
  // Circuits a point without protection can be connected to (R-TOPO-02).
  const codes = useMemo(
    () =>
      analyse(project)
        .circuits.filter((c) => c.code)
        .map((c) => ({ code: c.code, label: c.circuit?.label ?? '' })),
    [project],
  );
  return (
    <div className="checks">
      <p className="muted small pad">{t('checks.disclaimer')}</p>
      {findings.length === 0 ? (
        <p className="pad ok">{t('checks.ok')}</p>
      ) : (
        <ul>
          {findings.map((f, i) => {
            const info = ruleInfo(f.ruleId, project.meta.standard);
            const action = findingAction(f, project);
            return (
              <li key={i} className={`finding ${f.severity}`} data-testid={`finding-${f.ruleId}`}>
                <button
                  className="finding-head"
                  onClick={() => {
                    setOpen(open === i ? null : i);
                    if (f.targets.length || f.sheetId)
                      onShow(
                        f.targets,
                        f.sheetId ?? (f.targets[0] ? project.elements[f.targets[0]]?.sheetId : undefined),
                      );
                  }}
                >
                  <span className="sev" aria-label={f.severity}>
                    {SEV_ICON[f.severity]}
                  </span>
                  <span className="msg">{pick(f.message, locale)}</span>
                  <span className="rule">{f.ruleId}</span>
                  {info && !info.verified && <span className="badge">{t('checks.unverified')}</span>}
                </button>
                {open === i && info && (
                  <div className="finding-body">
                    <p>
                      <strong>
                        {t('checks.why')}
                        {colon}
                      </strong>
                      {pick(info.why, locale)}
                    </p>
                    <p>
                      <strong>
                        {t('checks.fix')}
                        {colon}
                      </strong>
                      {pick(info.fix, locale)}
                    </p>
                    {action?.assistant && onFix && (
                      <button className="fix-btn" onClick={() => onFix(f)} data-testid={`fix-${f.ruleId}`}>
                        {t('checks.askAssistant')}
                      </button>
                    )}
                    {onGoto &&
                      action?.buttons.map((b) =>
                        b.to === 'attach' ? (
                          <select
                            key={b.to}
                            className="fix-btn"
                            value=""
                            onChange={(e) => e.target.value && onGoto(f, b, e.target.value)}
                            data-testid={`goto-${f.ruleId}-attach`}
                          >
                            <option value="">{t('checks.goAttach')}</option>
                            {codes.map((c) => (
                              <option key={c.code} value={c.code}>
                                {c.code} {c.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <button
                            key={b.to}
                            className="fix-btn"
                            onClick={() => onGoto(f, b)}
                            data-testid={`goto-${f.ruleId}-${b.to}`}
                          >
                            {t(b.quick ? QUICK_LABEL[b.quick] : GOTO_LABEL[b.to], {
                              from: b.from ?? '',
                              tag: b.arg ?? '',
                            })}
                          </button>
                        ),
                      )}
                    {reporter && <ReportRule f={f} t={t} reporter={reporter} />}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function BottomPanel(props: {
  tab: PanelTab;
  onTab(t: PanelTab | null): void;
  editor: Editor;
  project: Project;
  analysis: Analysis;
  findings: Finding[];
  t: Translate;
  /** Interface language. */
  locale: UiLocale;
  onShow(ids: Id[], sheetId: Id | undefined): void;
  /** Ask the assistant to fix a finding. */
  onFix?(f: Finding): void;
  /** Take the user where they fix a finding the assistant cannot. */
  onGoto?(f: Finding, b: FindingButton, pick?: string): void;
  /** "Report this rule", when the server takes reports. */
  reporter?: RuleReporter;
}) {
  const { t, findings } = props;
  const errors = findings.filter((f) => f.severity === 'error').length;
  return (
    <section
      className="bottom-panel"
      aria-label={
        props.tab === 'circuits'
          ? t('panel.circuits')
          : t('panel.checks', { std: t(`std.${props.project.meta.standard}`) })
      }
    >
      <div className="panel-tabs">
        <div className="panel-tablist" role="tablist">
          <button
            role="tab"
            aria-selected={props.tab === 'circuits'}
            onClick={() => props.onTab('circuits')}
            data-testid="tab-circuits"
          >
            {t('panel.circuits')} <span className="count">{props.analysis.circuits.length}</span>
          </button>
          <button
            role="tab"
            aria-selected={props.tab === 'checks'}
            onClick={() => props.onTab('checks')}
            data-testid="tab-checks"
          >
            {t('panel.checks', { std: t(`std.${props.project.meta.standard}`) })}{' '}
            <span className={`count${errors ? ' bad' : ''}`}>{findings.length}</span>
          </button>
        </div>
        <span className="grow" />
        <button className="link" onClick={() => props.onTab(null)}>
          {t('panel.hide')}
        </button>
      </div>
      <div className="panel-body">
        {props.tab === 'circuits' ? (
          <CircuitTable
            editor={props.editor}
            project={props.project}
            analysis={props.analysis}
            t={t}
            locale={contentLocale(props.locale)}
            onShow={(ids, s) => props.onShow(ids, s)}
          />
        ) : (
          <Checks
            findings={findings}
            project={props.project}
            t={t}
            locale={props.locale}
            onShow={props.onShow}
            {...(props.onFix ? { onFix: props.onFix } : {})}
            {...(props.onGoto ? { onGoto: props.onGoto } : {})}
            {...(props.reporter ? { reporter: props.reporter } : {})}
          />
        )}
      </div>
    </section>
  );
}
